import { type ReactNode } from "react"

import { Box, Typography, Button } from '@mui/material'
import { useNavigate } from "react-router-dom"

import { useUser } from "../hooks/use-user"

import type { UserRole } from "../api/types"

type RoleProtectedRouteProps = {
  children: ReactNode
  allowedRoles: UserRole[]
  fallbackPath?: string
}

export default function RoleProtectedRoute({
  children,
  allowedRoles,
  fallbackPath = "/"
}: RoleProtectedRouteProps) {
  const { user, loading } = useUser()
  const navigate = useNavigate()

  if (loading) {
    return null // Let parent ProtectedRoute handle loading
  }

  if (!user || !allowedRoles.includes(user.role)) {
    return (
      <Box
        sx={{
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 2,
          p: 4,
        }}
      >
        <Typography variant="h4" color="error" gutterBottom>
          Access Denied
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center">
          You don't have permission to access this page.
          {user?.role === 'doctor' ? ' This section is restricted to administrators.' : ''}
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate(fallbackPath)}
          sx={{ mt: 2 }}
        >
          Go Back
        </Button>
      </Box>
    )
  }

  return <>{children}</>
}